import React, { useCallback, useContext, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { TooltipProvider } from '@onward-delivery/design';
import { UPDATE_CLIENT_BY_ID } from '@/components/admin/AdminUsers/mutations';
import { CorpAdminContext } from '../store';
import { useCarrierRows } from './hooks';
import { signsInOnOpen } from './utils';
import Carriers from './index';

const CarriersTab = ({ search, shipperIds, onOpenDetails, onSignIn }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks || {};
    const {
        rows,
        shipperGroupsByCarrier,
        marketplaceShippers,
        flexCentersByContractor,
        contractorsByFlexCenter,
        defaultPricingByCarrier,
        defaultIntegrationsByCarrier,
        loading,
        error,
    } = useCarrierRows();
    const topLevelRows = useMemo(
        () =>
            rows.filter((row) => {
                const sitsUnderFlexCenter = row.isContractor && !row.owned;

                return !sitsUnderFlexCenter;
            }),
        [rows]
    );

    // Returns ClientFields, so the cached client, and the row built from it, update without a refetch.
    const [updateClient] = useMutation(UPDATE_CLIENT_BY_ID);

    const toggleActive = useCallback(
        async (row, approved) => {
            try {
                const updateResponse = await updateClient({
                    variables: { client_id: row.client_id, update: { approved } },
                });
                const affectedRows = updateResponse.data?.update_clients?.affected_rows;
                if (!affectedRows) throw new Error(`No client updated for ${row.client_id}`);
            } catch (error) {
                Sentry.captureException(error);
                setNotification?.({ severity: 'error', message: `Failed to update ${row.business_name}` });
            }
        },
        [updateClient, setNotification]
    );

    const openDetails = useCallback(
        (row) =>
            onOpenDetails?.({
                context: row.isContractor ? 'CONTRACTOR' : 'CARRIER',
                clientId: row.client_id,
                clientName: row.business_name,
            }),
        [onOpenDetails]
    );

    const openCarrier = useCallback(
        (row) => {
            if (signsInOnOpen(row)) return onSignIn?.(row);

            return openDetails(row);
        },
        [onSignIn, openDetails]
    );

    // The scope element stays bare: `.onward-design .flex` matches descendants only.
    return (
        <div className="onward-design" style={{ width: '100%', height: '100%' }}>
            {/* Radix tooltips need a provider ancestor; one for the table instead of one per cell. */}
            <TooltipProvider delayDuration={200}>
                <div className="flex h-full w-full flex-col">
                    <Carriers
                        rows={topLevelRows}
                        search={search}
                        shipperIds={shipperIds}
                        loading={loading}
                        error={error}
                        shipperGroupsByCarrier={shipperGroupsByCarrier}
                        marketplaceShippers={marketplaceShippers}
                        flexCentersByContractor={flexCentersByContractor}
                        contractorsByFlexCenter={contractorsByFlexCenter}
                        defaultPricingByCarrier={defaultPricingByCarrier}
                        defaultIntegrationsByCarrier={defaultIntegrationsByCarrier}
                        onToggleActive={toggleActive}
                        onOpen={openCarrier}
                        onDetails={openDetails}
                    />
                </div>
            </TooltipProvider>
        </div>
    );
};

export default CarriersTab;
